import { useMemo } from 'react'

import { getOverallScore, getPlanQualityRows, getPlanQualityScores } from '../../../lib'
import { useCockpitFinancialPlanQuality, useFinSobCategoryBreakdown } from '../../../model/hooks'
import { useCockpitSettings } from '../../../model/hooks/useCockpitSettings'

import { useAchievementScoreRows } from './useAchievementScoreRows'

/** The scores the tile's two views wear on their tabs. The header's switch and the expanded
 *  tile's export both read them, so the strip and the deck never disagree.
 *
 *  Both badges read the Total row of one breakdown, which already carries the aggregate for
 *  whatever the offering filter admits — every offering's rows when `Overall` is selected, since
 *  `getFilteredFinSobRows` lets them all through. That aggregate is what the tables' topmost row
 *  shows too, and both pipelines score their own figures, so a badge can no longer contradict the
 *  table it sits above. */
export const useRevenueAndSobViewScores = (isManagerCockpitMode: boolean) => {
  const { isPeriodOpen } = useCockpitSettings()
  const { rows } = useFinSobCategoryBreakdown()
  const { achievementScoreScores: pillarScores } = useAchievementScoreRows(rows)
  const planQuality = useCockpitFinancialPlanQuality()

  /** The manager cockpit renders Plan Quality as the cockpit's own category table rather than the
   *  partner page's pillar cards, so it scores off the same flat category breakdown the table shows
   *  — keeping the switcher badge and the rows in agreement. */
  const cockpitPlanQualityScores = useMemo(
    () => getPlanQualityScores(getPlanQualityRows(rows)),
    [rows]
  )

  /** The Achievement badge averages the two pillar scores. For a period still open there is
   *  nothing scored anywhere, and the tab stays bare. */
  const achievementScore = isPeriodOpen
    ? undefined
    : getOverallScore(pillarScores.financial, pillarScores.sob)

  /** The badge follows whichever Plan Quality view the tab actually opens, so it never contradicts
   *  what it leads to: the cockpit's own category scores in manager mode, the partner page's pillar
   *  score otherwise. Shown regardless of `IsPlanned` — see the README's Plan Quality note. */
  const planQualityScore = isManagerCockpitMode
    ? getOverallScore(cockpitPlanQualityScores.financial, cockpitPlanQualityScores.sob)
    : planQuality.overall.score

  return { achievementScore, planQualityScore, planQuality }
}
